/**
 * Static demo data — used by DemoDataInjector to populate React Query caches
 * when demo mode is active. No database writes, no API calls.
 *
 * All IDs are valid UUID v4 format (de00000x-prefix) so they don't crash
 * server endpoints if a query slips through (returns empty, not 500).
 */
import type {
  TodayBundle,
  Task,
  ProjectSummary,
  InboxItemWithSuggestions,
  SuggestionsDigest,
  User,
  MailAccount,
  MailFolder,
  MailMessage,
  GetMailUnreadSummary200,
  GetMailPendingActionsSummary200,
  DriftAlert,
} from "@workspace/api-client-react";
import type { SimpleLabel } from "@/lib/labels-api";

function daysFromNow(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  d.setHours(9, 0, 0, 0);
  return d.toISOString();
}
function daysAgo(n: number): string {
  return daysFromNow(-n);
}

// ── IDs ──────────────────────────────────────────────────────────────────────
// Valid UUID v4 format to avoid server-side UUID parse errors.
const P1 = "de00000b-0000-4000-8000-000000000001"; // artemia
const P2 = "de00000b-0000-4000-8000-000000000002"; // campagne
const P3 = "de00000b-0000-4000-8000-000000000003"; // podcast
const P4 = "de00000b-0000-4000-8000-000000000004"; // prospe

const T1  = "de00000c-0000-4000-8000-000000000001";
const T2  = "de00000c-0000-4000-8000-000000000002";
const T3  = "de00000c-0000-4000-8000-000000000003";
const T4  = "de00000c-0000-4000-8000-000000000004";
const T5  = "de00000c-0000-4000-8000-000000000005";
const T6  = "de00000c-0000-4000-8000-000000000006";
const T7  = "de00000c-0000-4000-8000-000000000007";
const T8  = "de00000c-0000-4000-8000-000000000008";
const T9  = "de00000c-0000-4000-8000-000000000009";
const T10 = "de00000c-0000-4000-8000-00000000000a";
const T11 = "de00000c-0000-4000-8000-00000000000b";
const T12 = "de00000c-0000-4000-8000-00000000000c";
const T13 = "de00000c-0000-4000-8000-00000000000d";
const T14 = "de00000c-0000-4000-8000-00000000000e";
const T15 = "de00000c-0000-4000-8000-00000000000f";
const T16 = "de00000c-0000-4000-8000-000000000010";
const T17 = "de00000c-0000-4000-8000-000000000011";
const T18 = "de00000c-0000-4000-8000-000000000012";
const T19 = "de00000c-0000-4000-8000-000000000013";

const L_CLIENT   = "de00000a-0000-4000-8000-000000000001";
const L_URGENT   = "de00000a-0000-4000-8000-000000000002";
const L_PODCAST  = "de00000a-0000-4000-8000-000000000003";
const L_REPONDRE = "de00000a-0000-4000-8000-000000000004";

// ── Labels ────────────────────────────────────────────────────────────────────
const LABEL_CLIENT:   SimpleLabel = { id: L_CLIENT,   name: "Client",      color: "#ef4444" };
const LABEL_URGENT:   SimpleLabel = { id: L_URGENT,   name: "Urgent",      color: "#f97316" };
const LABEL_PODCAST:  SimpleLabel = { id: L_PODCAST,  name: "Podcast",     color: "#a855f7" };
const LABEL_REPONDRE: SimpleLabel = { id: L_REPONDRE, name: "À répondre",  color: "#3b82f6" };

// task ID → labels attached to it
export const DEMO_TASK_LABELS_BY_TASK_ID: Record<string, SimpleLabel[]> = {
  [T1]:  [LABEL_CLIENT, LABEL_URGENT],
  [T2]:  [LABEL_CLIENT, LABEL_REPONDRE],
  [T3]:  [LABEL_CLIENT],
  [T4]:  [LABEL_CLIENT, LABEL_URGENT],
  [T7]:  [LABEL_CLIENT, LABEL_URGENT],
  [T8]:  [LABEL_CLIENT],
  [T9]:  [LABEL_CLIENT, LABEL_REPONDRE],
  [T10]: [LABEL_CLIENT, LABEL_URGENT],
  [T11]: [LABEL_PODCAST],
  [T12]: [LABEL_PODCAST],
  [T13]: [LABEL_PODCAST],
  [T14]: [LABEL_URGENT],
  [T15]: [LABEL_URGENT],
};

// ── Projects ─────────────────────────────────────────────────────────────────
export const DEMO_PROJECTS: ProjectSummary[] = [
  {
    id: P1,
    name: "Refonte site web Artemia Group",
    description: "Refonte complète du site vitrine : nouvelle charte, nouveaux textes, intégration CMS.",
    goal: "Mettre en ligne le nouveau site avant le 22 mai.",
    status: "active",
    priority: "high",
    deadline: daysFromNow(18),
    createdAt: daysAgo(12),
    openTaskCount: 4,
    doneTaskCount: 2,
    nextAction: null,
  },
  {
    id: P2,
    name: "Campagne lancement été 2026",
    description: "Campagne multi-supports (print + digital) pour le lancement de la gamme été.",
    goal: "Livrer tous les visuels validés avant le 15 mai pour partir à l'impression.",
    status: "active",
    priority: "high",
    deadline: daysFromNow(11),
    createdAt: daysAgo(14),
    openTaskCount: 4,
    doneTaskCount: 1,
    nextAction: null,
  },
  {
    id: P3,
    name: "Podcast « Les Créatifs »",
    description: "Podcast mensuel qui donne la parole à des créatifs indépendants.",
    goal: "Publier 1 épisode par mois et atteindre 500 écoutes/épisode d'ici l'été.",
    status: "active",
    priority: "low",
    deadline: null,
    createdAt: daysAgo(30),
    openTaskCount: 3,
    doneTaskCount: 1,
    nextAction: null,
  },
  {
    id: P4,
    name: "Prospection nouveaux clients Q2",
    description: "Relancer la prospection commerciale pour remplir le carnet de commandes de juin–août.",
    goal: "Signer 2 nouveaux contrats d'ici fin juin.",
    status: "active",
    priority: "medium",
    deadline: daysFromNow(57),
    createdAt: daysAgo(10),
    openTaskCount: 4,
    doneTaskCount: 0,
    nextAction: null,
  },
];

// ── Tasks ─────────────────────────────────────────────────────────────────────
export const DEMO_TASKS: Task[] = [
  // Artemia
  { id: T1,  projectId: P1, title: "Valider la maquette Figma avec le client",           status: "todo", priority: "high",     dueDate: daysFromNow(0),  estimatedMinutes: 60,  energy: "medium", createdAt: daysAgo(5),  completedAt: null },
  { id: T2,  projectId: P1, title: "Rédiger les textes de la page Accueil",               status: "todo", priority: "medium",   dueDate: daysFromNow(2),  estimatedMinutes: 90,  energy: "high",   createdAt: daysAgo(4),  completedAt: null },
  { id: T3,  projectId: P1, title: "Intégrer la charte graphique dans le CMS",            status: "todo", priority: "high",     dueDate: daysFromNow(6),  estimatedMinutes: 120, energy: "high",   createdAt: daysAgo(4),  completedAt: null },
  { id: T4,  projectId: P1, title: "Recette complète avant mise en ligne",                status: "todo", priority: "critical", dueDate: daysFromNow(16), estimatedMinutes: 180, energy: "high",   createdAt: daysAgo(3),  completedAt: null },
  { id: T5,  projectId: P1, title: "Kick-off et brief client envoyé",                    status: "done", priority: "low",      dueDate: null,            estimatedMinutes: 30,  energy: "low",    createdAt: daysAgo(10), completedAt: daysAgo(5) },
  { id: T6,  projectId: P1, title: "Audit du site existant terminé",                     status: "done", priority: "medium",   dueDate: null,            estimatedMinutes: 60,  energy: "medium", createdAt: daysAgo(8),  completedAt: daysAgo(3) },
  // Campagne
  { id: T7,  projectId: P2, title: "Créer le brief créatif de la campagne",              status: "todo", priority: "critical", dueDate: daysFromNow(0),  estimatedMinutes: 45,  energy: "high",   createdAt: daysAgo(3),  completedAt: null },
  { id: T8,  projectId: P2, title: "Sélectionner les visuels principaux (banque d'images)", status: "todo", priority: "high",  dueDate: daysFromNow(3),  estimatedMinutes: 30,  energy: "low",    createdAt: daysAgo(3),  completedAt: null },
  { id: T9,  projectId: P2, title: "Rédiger les accroches pour les 3 formats",           status: "todo", priority: "medium",   dueDate: daysFromNow(5),  estimatedMinutes: 60,  energy: "high",   createdAt: daysAgo(2),  completedAt: null },
  { id: T10, projectId: P2, title: "Envoyer BAT à l'imprimeur",                          status: "todo", priority: "high",     dueDate: daysFromNow(10), estimatedMinutes: 20,  energy: "low",    createdAt: daysAgo(2),  completedAt: null },
  // Podcast
  { id: T11, projectId: P3, title: "Monter l'épisode 2 (invitée : Léa Fontaine)",        status: "todo", priority: "medium",   dueDate: daysFromNow(4),  estimatedMinutes: 120, energy: "high",   createdAt: daysAgo(5),  completedAt: null },
  { id: T12, projectId: P3, title: "Préparer les questions pour l'épisode 3",            status: "todo", priority: "low",      dueDate: daysFromNow(6),  estimatedMinutes: 40,  energy: "medium", createdAt: daysAgo(4),  completedAt: null },
  { id: T13, projectId: P3, title: "Enregistrer l'épisode 3 avec Thomas Vidal",          status: "todo", priority: "medium",   dueDate: daysFromNow(7),  estimatedMinutes: 90,  energy: "high",   createdAt: daysAgo(4),  completedAt: null },
  // Prospection
  { id: T14, projectId: P4, title: "Mettre à jour le deck de présentation",              status: "todo",      priority: "high",     dueDate: daysFromNow(0),  estimatedMinutes: 60,  energy: "high",   createdAt: daysAgo(2),  completedAt: null },
  { id: T15, projectId: P4, title: "Relancer 3 leads chauds de mars",                   status: "todo",      priority: "high",     dueDate: daysFromNow(1),  estimatedMinutes: 30,  energy: "medium", createdAt: daysAgo(2),  completedAt: null },
  { id: T16, projectId: P4, title: "Envoyer 10 messages de prospection cette semaine",   status: "todo",      priority: "medium",   dueDate: daysFromNow(4),  estimatedMinutes: 60,  energy: "medium", createdAt: daysAgo(1),  completedAt: null },
  { id: T17, projectId: P4, title: "Qualifier la liste de prospects Salons été",         status: "todo",      priority: "low",      dueDate: daysFromNow(11), estimatedMinutes: 45,  energy: "low",    createdAt: daysAgo(1),  completedAt: null },
  // Délégation
  { id: T18, projectId: P1, title: "Rédiger la FAQ technique du nouveau site",           status: "delegated", priority: "medium",   dueDate: daysFromNow(5),  estimatedMinutes: 90,  energy: "medium", createdAt: daysAgo(6),  completedAt: null, assignedToName: "Camille Renard", assignedToEmail: "c.renard@artemia.fr", delegationNote: "Camille a toutes les infos techniques — merci de préparer un draft avant jeudi.", delegatedAt: daysAgo(3) },
  { id: T19, projectId: P2, title: "Recadrer les 12 visuels au format Instagram",       status: "delegated", priority: "high",     dueDate: daysFromNow(2),  estimatedMinutes: 60,  energy: "low",    createdAt: daysAgo(4),  completedAt: null, assignedToName: "Lucas Petit",    assignedToEmail: "l.petit@artemia.fr",  delegationNote: "Format 1080×1080, fond blanc, export PNG + JPG.",                               delegatedAt: daysAgo(2) },
];

// ── TodayBundle ───────────────────────────────────────────────────────────────
export function getDemoToday(): TodayBundle {
  const todayTasks = DEMO_TASKS.filter(
    (t) => t.status === "todo" && t.dueDate && new Date(t.dueDate) <= new Date(Date.now() + 24 * 3600 * 1000)
  );
  return {
    top: todayTasks[0]
      ? { task: todayTasks[0], project: DEMO_PROJECTS.find((p) => p.id === todayTasks[0].projectId) ?? null, score: 95, explanation: "Tâche critique échéant aujourd'hui." }
      : null,
    nextActions: todayTasks.slice(1, 4).map((t, i) => ({
      task: t,
      project: DEMO_PROJECTS.find((p) => p.id === t.projectId) ?? null,
      score: 80 - i * 10,
      explanation: "Priorité haute, échéance proche.",
    })),
    dueToday: todayTasks,
    overdue: [],
    quickWins: DEMO_TASKS.filter((t) => t.status === "todo" && (t.estimatedMinutes ?? 60) <= 30).slice(0, 2),
    projectsWithoutNextAction: [],
  };
}

// ── Inbox ─────────────────────────────────────────────────────────────────────
export const DEMO_INBOX: InboxItemWithSuggestions[] = [
  {
    id: "de00000d-0000-4000-8000-000000000001",
    sourceType: "email",
    rawText: "Sophie Lehmann demande un devis pour une refonte identité visuelle (logo, charte, papeterie).",
    transcriptText: null,
    audioUrl: null,
    status: "analyzed",
    aiSummary: "Sophie Lehmann demande un devis pour une refonte identité visuelle (logo, charte, papeterie). À rappeler cette semaine.",
    aiDetectedType: "task",
    aiConfidence: 0.94,
    createdAt: daysAgo(1),
    mailMessageId: null,
    suggestions: [
      {
        id: "de00000e-0000-4000-8000-000000000001",
        inboxItemId: "de00000d-0000-4000-8000-000000000001",
        type: "task",
        status: "pending",
        payload: { type: "task", title: "Préparer devis refonte identité pour Acmé Conseil", priority: "high", estimated_minutes: 30 },
        createdAt: daysAgo(1),
      },
    ],
  },
  {
    id: "de00000d-0000-4000-8000-000000000002",
    sourceType: "email",
    rawText: "Pierre Dumont propose le 14 ou 15 mai pour le tournage véhicules à Grenoble. Confirmation attendue avant vendredi.",
    transcriptText: null,
    audioUrl: null,
    status: "analyzed",
    aiSummary: "Pierre Dumont propose le 14 ou 15 mai pour le tournage véhicules à Grenoble. Confirmation attendue avant vendredi.",
    aiDetectedType: "event",
    aiConfidence: 0.88,
    createdAt: daysAgo(2),
    mailMessageId: null,
    suggestions: [],
  },
  {
    id: "de00000d-0000-4000-8000-000000000003",
    sourceType: "email",
    rawText: "Invitation à la Conférence Créativité & Tech le 12 juin à Paris. Inscription gratuite avant le 30 mai.",
    transcriptText: null,
    audioUrl: null,
    status: "new",
    aiSummary: "Invitation à la Conférence Créativité & Tech le 12 juin à Paris. Inscription gratuite avant le 30 mai.",
    aiDetectedType: "event",
    aiConfidence: 0.91,
    createdAt: daysAgo(2),
    mailMessageId: null,
    suggestions: [],
  },
  {
    id: "de00000d-0000-4000-8000-000000000004",
    sourceType: "voice",
    rawText: null,
    transcriptText: "Idée d'accroche pour l'intro de l'épisode 3 : question directe à l'auditeur sur les idées qui arrivent au mauvais moment.",
    audioUrl: null,
    status: "analyzed",
    aiSummary: "Idée d'accroche pour l'intro de l'épisode 3 : question directe à l'auditeur.",
    aiDetectedType: "note",
    aiConfidence: 0.87,
    createdAt: daysAgo(3),
    mailMessageId: null,
    suggestions: [],
  },
  {
    id: "de00000d-0000-4000-8000-000000000005",
    sourceType: "text",
    rawText: "Appeler Marc Girard avant jeudi — il attend le brief packaging Q3 pour démarrer la production.",
    transcriptText: null,
    audioUrl: null,
    status: "analyzed",
    aiSummary: "Appeler Marc Girard avant jeudi pour le brief packaging Q3.",
    aiDetectedType: "task",
    aiConfidence: 0.96,
    createdAt: daysAgo(3),
    mailMessageId: null,
    suggestions: [
      {
        id: "de00000e-0000-4000-8000-000000000002",
        inboxItemId: "de00000d-0000-4000-8000-000000000005",
        type: "task",
        status: "pending",
        payload: { type: "task", title: "Appeler Marc Girard — brief packaging Q3", priority: "high", estimated_minutes: 15 },
        createdAt: daysAgo(3),
      },
    ],
  },
];

// ── Suggestions digest ────────────────────────────────────────────────────────
export const DEMO_SUGGESTIONS_DIGEST: SuggestionsDigest = {
  suggestions: DEMO_INBOX.flatMap((item) =>
    item.suggestions.map((s) => ({
      ...s,
      inboxSource: item,
      matchedProject: null,
      contacts: [],
    }))
  ),
  people: [
    { name: "Sophie Lehmann" },
    { name: "Pierre Dumont" },
    { name: "Marc Girard" },
  ],
};

// ── Demo user ─────────────────────────────────────────────────────────────────
export const DEMO_USER: User = {
  id: "de00000f-0000-4000-8000-000000000001",
  email: "demo@onenext.app",
  name: "Béatrice Dupont",
};

// ── Mail ──────────────────────────────────────────────────────────────────────
export const DEMO_MAIL_ACCOUNT_ID = "de00000f-0000-4000-8000-000000000002";
const DEMO_FOLDER_INBOX_ID  = "de000010-0000-4000-8000-000000000001";
const DEMO_FOLDER_SENT_ID   = "de000010-0000-4000-8000-000000000002";
const DEMO_FOLDER_DRAFTS_ID = "de000010-0000-4000-8000-000000000003";

export const DEMO_MAIL_ACCOUNTS: MailAccount[] = [
  {
    id: DEMO_MAIL_ACCOUNT_ID,
    label: "Pro — demo@artemia.fr",
    emailAddress: "beatrice@artemia.fr",
    fromName: "Béatrice Dupont",
    signature: null,
    isDefault: true,
    imapHost: "demo.imap.local",
    imapPort: 993,
    imapSecure: true,
    imapUsername: "beatrice@artemia.fr",
    smtpHost: "demo.smtp.local",
    smtpPort: 587,
    smtpSecure: true,
    smtpUsername: "beatrice@artemia.fr",
    autoAnalyzeFromContains: [],
    autoSync: false,
    analysisPaused: false,
    taskCreationWindowDays: 14,
    dailyAiAnalysisCap: 200,
    dailyAiUsageToday: 12,
    dailyAiUsageRemaining: 188,
    firstSyncCompletedAt: daysAgo(30),
    lastSyncAt: daysAgo(0),
    lastError: null,
    syncStatus: "ok",
    createdAt: daysAgo(30),
    updatedAt: daysAgo(0),
  },
];

export const DEMO_MAIL_FOLDERS: MailFolder[] = [
  { id: DEMO_FOLDER_INBOX_ID,  accountId: DEMO_MAIL_ACCOUNT_ID, path: "INBOX",      name: "INBOX",  role: "inbox",  delimiter: "/", unreadCount: 4, totalCount: 8 },
  { id: DEMO_FOLDER_SENT_ID,   accountId: DEMO_MAIL_ACCOUNT_ID, path: "Sent",       name: "Sent",   role: "sent",   delimiter: "/", unreadCount: 0, totalCount: 6 },
  { id: DEMO_FOLDER_DRAFTS_ID, accountId: DEMO_MAIL_ACCOUNT_ID, path: "Drafts",     name: "Drafts", role: "drafts", delimiter: "/", unreadCount: 0, totalCount: 1 },
];

function mailMsg(
  id: string,
  folderId: string,
  uid: number,
  from: { name: string; address: string },
  subject: string,
  snippet: string,
  isUnread: boolean,
  receivedAt: string,
  triageCategory: MailMessage["triageCategory"],
  triageSummary: string,
): MailMessage {
  return {
    id,
    accountId: DEMO_MAIL_ACCOUNT_ID,
    folderId,
    uid,
    messageId: `<${id}@demo.local>`,
    fromName: from.name,
    fromAddress: from.address,
    toAddresses: [{ name: "Béatrice Dupont", address: "beatrice@artemia.fr" }],
    ccAddresses: [],
    bccAddresses: [],
    subject,
    snippet,
    isUnread,
    isFlagged: false,
    isAnswered: false,
    isDraft: false,
    hasAttachments: false,
    receivedAt,
    triageCategory,
    triageConfidence: 0.92,
    triageSummary,
    triageProjectId: null,
    triageProjectHint: null,
    triageSource: "ai",
    triagedAt: receivedAt,
    triageDeferredAt: null,
    isOldThreadCandidate: false,
    oldThreadResolution: null,
    listUnsubscribe: null,
  };
}

export const DEMO_MAIL_MESSAGES: MailMessage[] = [
  mailMsg(
    "de000011-0000-4000-8000-000000000001", DEMO_FOLDER_INBOX_ID, 1001,
    { name: "Sophie Lehmann", address: "s.lehmann@acmeconseil.fr" },
    "Demande de devis — Refonte identité visuelle",
    "Bonjour Béatrice, suite à notre échange lors du salon, je souhaiterais obtenir un devis pour une refonte complète de notre identité visuelle…",
    true, daysAgo(1), "action_required",
    "Sophie Lehmann demande un devis pour une refonte identité visuelle (logo, charte, papeterie). À rappeler cette semaine.",
  ),
  mailMsg(
    "de000011-0000-4000-8000-000000000002", DEMO_FOLDER_INBOX_ID, 1002,
    { name: "Pierre Dumont", address: "p.dumont@studio-dumont.com" },
    "Tournage véhicules Grenoble — confirmation dates",
    "Béatrice, je peux vous proposer le 14 ou le 15 mai pour le tournage à Grenoble. Merci de confirmer avant vendredi afin de bloquer le matériel.",
    true, daysAgo(2), "action_required",
    "Confirmation attendue avant vendredi pour le tournage véhicules à Grenoble (14 ou 15 mai).",
  ),
  mailMsg(
    "de000011-0000-4000-8000-000000000003", DEMO_FOLDER_INBOX_ID, 1003,
    { name: "Marc Girard", address: "m.girard@packagingnord.fr" },
    "Brief packaging Q3 — relance",
    "Bonjour, je me permets de relancer car nous démarrons la production la semaine prochaine. Avez-vous le brief packaging Q3 à nous transmettre ?",
    true, daysAgo(2), "action_required",
    "Marc Girard attend le brief packaging Q3 pour démarrer la production. Action urgente requise.",
  ),
  mailMsg(
    "de000011-0000-4000-8000-000000000004", DEMO_FOLDER_INBOX_ID, 1004,
    { name: "Conférence Créativité & Tech", address: "noreply@creativitetech.fr" },
    "Invitation — Conférence Créativité & Tech, 12 juin Paris",
    "Nous avons le plaisir de vous inviter à la 5e édition de la Conférence Créativité & Tech, le 12 juin à Paris. Inscription gratuite avant le 30 mai.",
    true, daysAgo(3), "fyi",
    "Invitation à la Conférence Créativité & Tech le 12 juin à Paris. Inscription gratuite avant le 30 mai.",
  ),
  mailMsg(
    "de000011-0000-4000-8000-000000000005", DEMO_FOLDER_INBOX_ID, 1005,
    { name: "Thomas Vidal", address: "thomas.vidal@gmail.com" },
    "Re : Épisode 3 du podcast — on confirme le 19 ?",
    "Salut Béatrice ! Super pour l'épisode 2. Pour le 3, le 19 mai me convient parfaitement. Je prépare quelques anecdotes sur mon parcours.",
    false, daysAgo(4), "awaiting_reply",
    "Thomas Vidal confirme sa disponibilité pour l'enregistrement du podcast épisode 3 le 19 mai.",
  ),
  mailMsg(
    "de000011-0000-4000-8000-000000000006", DEMO_FOLDER_INBOX_ID, 1006,
    { name: "Artemia Group — Comptabilité", address: "compta@artemia.fr" },
    "Facture #2026-041 — règlement reçu",
    "Bonjour, nous vous confirmons la réception du règlement de la facture #2026-041 d'un montant de 3 200 €. Merci pour votre confiance.",
    false, daysAgo(5), "fyi",
    "Confirmation de règlement de la facture #2026-041 (3 200 €). Aucune action requise.",
  ),
  mailMsg(
    "de000011-0000-4000-8000-000000000007", DEMO_FOLDER_INBOX_ID, 1007,
    { name: "Léa Fontaine", address: "lea.fontaine@independante.io" },
    "Épisode 2 — fichiers audio livrés ✓",
    "Béatrice, les fichiers audio de l'épisode 2 sont disponibles dans le Drive partagé. N'hésite pas si tu as des retours avant le montage final.",
    false, daysAgo(6), "fyi",
    "Léa Fontaine a déposé les fichiers audio de l'épisode 2 dans le Drive. Pas d'action requise.",
  ),
  mailMsg(
    "de000011-0000-4000-8000-000000000008", DEMO_FOLDER_INBOX_ID, 1008,
    { name: "HubSpot", address: "notifications@hubspot.com" },
    "Votre essai HubSpot CRM expire dans 3 jours",
    "Votre essai gratuit HubSpot CRM expire dans 3 jours. Passez à un plan payant pour conserver vos données et continuer à gérer vos contacts.",
    false, daysAgo(7), "noise",
    "Notification marketing HubSpot. Pas d'action requise.",
  ),
];

export const DEMO_MAIL_UNREAD: GetMailUnreadSummary200 = { total: 4 };
export const DEMO_MAIL_PENDING: GetMailPendingActionsSummary200 = { total: 3 };

// ── CRM ───────────────────────────────────────────────────────────────────────
export interface DemoCrmContact {
  id: string;
  name: string;
  email: string;
  company: string | null;
  stage: string;
  assignedTo: string | null;
  notes: string | null;
  openTaskCount: number;
  lastMailAt: string | null;
  lastInteractionAt: string | null;
  daysSinceInteraction: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface DemoCrmFollowUp {
  id: string;
  name: string;
  email: string;
  company: string | null;
  alertReason: "waiting" | "overdue_followup" | "delegated_no_response" | "no_interaction" | "long_silence";
  lastInteractionAt: string | null;
  daysSinceInteraction: number | null;
  followUpAt: string | null;
  waitingForWhat: string | null;
  waitingSince: string | null;
}

const C1 = "de000013-0000-4000-8000-000000000001";
const C2 = "de000013-0000-4000-8000-000000000002";
const C3 = "de000013-0000-4000-8000-000000000003";
const C4 = "de000013-0000-4000-8000-000000000004";
const C5 = "de000013-0000-4000-8000-000000000005";
const C6 = "de000013-0000-4000-8000-000000000006";

export const DEMO_CRM_CONTACTS: DemoCrmContact[] = [
  {
    id: C1,
    name: "Sophie Lehmann",
    email: "s.lehmann@acmeconseil.fr",
    company: "Acmé Conseil",
    stage: "prospect",
    assignedTo: null,
    notes: "Rencontrée au salon Design & Com en avril. Cherche prestataire pour refonte identité complète.",
    openTaskCount: 2,
    lastMailAt: daysAgo(1),
    lastInteractionAt: daysAgo(1),
    daysSinceInteraction: 1,
    createdAt: daysAgo(14),
    updatedAt: daysAgo(1),
  },
  {
    id: C2,
    name: "Marc Girard",
    email: "m.girard@packagingnord.fr",
    company: "Packaging Nord",
    stage: "client",
    assignedTo: null,
    notes: "Client fidèle depuis 2 ans. Packaging saisonnier + campagnes print. Relance urgente brief Q3.",
    openTaskCount: 1,
    lastMailAt: daysAgo(2),
    lastInteractionAt: daysAgo(2),
    daysSinceInteraction: 2,
    createdAt: daysAgo(730),
    updatedAt: daysAgo(2),
  },
  {
    id: C3,
    name: "Pierre Dumont",
    email: "p.dumont@studio-dumont.com",
    company: "Studio Dumont",
    stage: "active",
    assignedTo: null,
    notes: "Prestataire vidéo partenaire. Tournage véhicules Grenoble en attente de confirmation.",
    openTaskCount: 1,
    lastMailAt: daysAgo(2),
    lastInteractionAt: daysAgo(2),
    daysSinceInteraction: 2,
    createdAt: daysAgo(90),
    updatedAt: daysAgo(2),
  },
  {
    id: C4,
    name: "Thomas Vidal",
    email: "thomas.vidal@gmail.com",
    company: null,
    stage: "active",
    assignedTo: null,
    notes: "Invité épisode 3 du podcast. Entrepreneur, spécialiste en innovation sociale.",
    openTaskCount: 1,
    lastMailAt: daysAgo(4),
    lastInteractionAt: daysAgo(4),
    daysSinceInteraction: 4,
    createdAt: daysAgo(21),
    updatedAt: daysAgo(4),
  },
  {
    id: C5,
    name: "Léa Fontaine",
    email: "lea.fontaine@independante.io",
    company: null,
    stage: "active",
    assignedTo: null,
    notes: "Invitée épisode 2. Designeuse indépendante. Livraison fichiers audio OK.",
    openTaskCount: 0,
    lastMailAt: daysAgo(6),
    lastInteractionAt: daysAgo(6),
    daysSinceInteraction: 6,
    createdAt: daysAgo(45),
    updatedAt: daysAgo(6),
  },
  {
    id: C6,
    name: "Julien Moreau",
    email: "j.moreau@designstudio.fr",
    company: "Design Studio Paris",
    stage: "prospect",
    assignedTo: null,
    notes: "Contacté lors de la conférence Créativité & Tech. Intéressé par collaboration sur projets retail.",
    openTaskCount: 0,
    lastMailAt: null,
    lastInteractionAt: daysAgo(22),
    daysSinceInteraction: 22,
    createdAt: daysAgo(22),
    updatedAt: daysAgo(22),
  },
];

export const DEMO_CRM_FOLLOW_UPS: DemoCrmFollowUp[] = [
  {
    id: C6,
    name: "Julien Moreau",
    email: "j.moreau@designstudio.fr",
    company: "Design Studio Paris",
    alertReason: "long_silence",
    lastInteractionAt: daysAgo(22),
    daysSinceInteraction: 22,
    followUpAt: null,
    waitingForWhat: null,
    waitingSince: null,
  },
  {
    id: C2,
    name: "Marc Girard",
    email: "m.girard@packagingnord.fr",
    company: "Packaging Nord",
    alertReason: "waiting",
    lastInteractionAt: daysAgo(2),
    daysSinceInteraction: 2,
    followUpAt: null,
    waitingForWhat: "Retour sur le brief packaging Q3",
    waitingSince: daysAgo(5),
  },
  {
    id: C1,
    name: "Sophie Lehmann",
    email: "s.lehmann@acmeconseil.fr",
    company: "Acmé Conseil",
    alertReason: "overdue_followup",
    lastInteractionAt: daysAgo(1),
    daysSinceInteraction: 1,
    followUpAt: daysAgo(0),
    waitingForWhat: null,
    waitingSince: null,
  },
];

// ── Drift Alerts ───────────────────────────────────────────────────────────────
export const DEMO_DRIFT_ALERTS: DriftAlert[] = [
  {
    id: "de000014-0000-4000-8000-000000000001",
    type: "delegated_no_response",
    referenceId: T18,
    referenceType: "task",
    metadata: {
      taskTitle: "Rédiger la FAQ technique du nouveau site",
      assignedToName: "Camille Renard",
      daysSince: 3,
    },
    detectedAt: daysAgo(1),
  },
  {
    id: "de000014-0000-4000-8000-000000000002",
    type: "project_inactive",
    referenceId: P3,
    referenceType: "project",
    metadata: {
      projectName: "Podcast « Les Créatifs »",
      daysSince: 8,
    },
    detectedAt: daysAgo(1),
  },
  {
    id: "de000014-0000-4000-8000-000000000003",
    type: "email_awaiting_reply",
    referenceId: "de000010-0000-4000-8000-000000000012",
    referenceType: "mail_message",
    metadata: {
      subject: "Devis refonte identité — Acmé Conseil",
      daysSince: 5,
    },
    detectedAt: daysAgo(0),
  },
  {
    id: "de000014-0000-4000-8000-000000000004",
    type: "document_without_action",
    referenceId: "de000015-0000-4000-8000-000000000001",
    referenceType: "library_document",
    metadata: {
      filename: "Brief_campagne_été_2026.pdf",
      daysSince: 6,
    },
    detectedAt: daysAgo(0),
  },
];

// ── Decision Log ───────────────────────────────────────────────────────────────
export interface DemoDecisionEntry {
  id: string;
  projectId: string | null;
  taskId: string | null;
  taskTitle: string | null;
  actionType: string;
  fromValue: string | null;
  toValue: string | null;
  reason: string | null;
  createdAt: string;
  currentTaskStatus: string | null;
}

export const DEMO_DECISION_LOG: DemoDecisionEntry[] = [
  {
    id: "de000016-0000-4000-8000-000000000001",
    projectId: P1,
    taskId: T18,
    taskTitle: "Rédiger la FAQ technique du nouveau site",
    actionType: "task_delegated",
    fromValue: null,
    toValue: "Camille Renard",
    reason: "Camille a l'expertise technique nécessaire, je me concentre sur la maquette.",
    createdAt: daysAgo(3),
    currentTaskStatus: "delegated",
  },
  {
    id: "de000016-0000-4000-8000-000000000002",
    projectId: P2,
    taskId: T19,
    taskTitle: "Recadrer les 12 visuels au format Instagram",
    actionType: "task_delegated",
    fromValue: null,
    toValue: "Lucas Petit",
    reason: "Tâche répétitive adaptée à un assistant — je supervise le résultat.",
    createdAt: daysAgo(2),
    currentTaskStatus: "delegated",
  },
  {
    id: "de000016-0000-4000-8000-000000000003",
    projectId: P4,
    taskId: T17,
    taskTitle: "Qualifier la liste de prospects Salons été",
    actionType: "task_postponed",
    fromValue: daysAgo(3),
    toValue: daysFromNow(11),
    reason: "Priorité déplacée sur les leads chauds de mars d'abord.",
    createdAt: daysAgo(3),
    currentTaskStatus: "todo",
  },
  {
    id: "de000016-0000-4000-8000-000000000004",
    projectId: P1,
    taskId: T4,
    taskTitle: "Recette complète avant mise en ligne",
    actionType: "priority_changed",
    fromValue: "high",
    toValue: "critical",
    reason: "Date de mise en ligne confirmée — aucune marge d'erreur possible.",
    createdAt: daysAgo(1),
    currentTaskStatus: "todo",
  },
  {
    id: "de000016-0000-4000-8000-000000000005",
    projectId: P1,
    taskId: T5,
    taskTitle: "Kick-off et brief client envoyé",
    actionType: "task_done",
    fromValue: null,
    toValue: null,
    reason: null,
    createdAt: daysAgo(5),
    currentTaskStatus: "done",
  },
  {
    id: "de000016-0000-4000-8000-000000000006",
    projectId: P3,
    taskId: null,
    taskTitle: "Contacter Radio Créateurs pour partenariat",
    actionType: "task_refused",
    fromValue: null,
    toValue: null,
    reason: "Hors périmètre pour Q2 — à reconsidérer en septembre.",
    createdAt: daysAgo(7),
    currentTaskStatus: null,
  },
];

// ── Library Documents ──────────────────────────────────────────────────────────
export interface DemoLibraryDocument {
  id: string;
  filename: string | null;
  contentType: string | null;
  sizeBytes: number | null;
  status: string;
  errorMessage: string | null;
  chunkCount: number;
  aiImportanceScore: number | null;
  aiAnalyzedAt: string | null;
  createdAt: string;
  sourceEmail: string | null;
  mailMessageId: string | null;
  mailReceivedAt: string | null;
  mailSubject: string | null;
}

export interface DemoDocumentAnalysis {
  id: string;
  aiSummary: string | null;
  aiImportanceScore: number | null;
  aiPersonsMentioned: { name: string; role: string | null }[] | null;
  aiDeadlines: { text: string; date: string | null }[] | null;
  aiDecisions: { text: string }[] | null;
  aiExtractedTaskCount: number | null;
  aiAnalyzedAt: string | null;
  links: { entityType: "contact" | "project" | "mail_message"; entityId: string; label: string; sub: string | null }[];
}

export interface DemoDocumentTask {
  id: string;
  title: string | null;
  rawText: string | null;
  status: string;
}

const DOC1 = "de000015-0000-4000-8000-000000000001";
const DOC2 = "de000015-0000-4000-8000-000000000002";
const DOC3 = "de000015-0000-4000-8000-000000000003";

export const DEMO_LIBRARY_STATUS = {
  lastSyncAt: daysAgo(0),
  counts: { total: 3, ready: 3, pending: 0, error: 0 },
};

export const DEMO_LIBRARY_DOCUMENTS: DemoLibraryDocument[] = [
  {
    id: DOC1,
    filename: "Brief_campagne_été_2026.pdf",
    contentType: "application/pdf",
    sizeBytes: 345600,
    status: "ready",
    errorMessage: null,
    chunkCount: 8,
    aiImportanceScore: 92,
    aiAnalyzedAt: daysAgo(1),
    createdAt: daysAgo(6),
    sourceEmail: "m.girard@packagingnord.fr",
    mailMessageId: null,
    mailReceivedAt: daysAgo(6),
    mailSubject: "Brief campagne packaging été 2026 — Packaging Nord",
  },
  {
    id: DOC2,
    filename: "Contrat_prestation_Artemia_2026.docx",
    contentType: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    sizeBytes: 128000,
    status: "ready",
    errorMessage: null,
    chunkCount: 5,
    aiImportanceScore: 87,
    aiAnalyzedAt: daysAgo(3),
    createdAt: daysAgo(10),
    sourceEmail: null,
    mailMessageId: null,
    mailReceivedAt: null,
    mailSubject: null,
  },
  {
    id: DOC3,
    filename: "Notes_réunion_logistique_Sophie_Martin.txt",
    contentType: "text/plain",
    sizeBytes: 18400,
    status: "ready",
    errorMessage: null,
    chunkCount: 3,
    aiImportanceScore: 74,
    aiAnalyzedAt: daysAgo(2),
    createdAt: daysAgo(8),
    sourceEmail: null,
    mailMessageId: null,
    mailReceivedAt: null,
    mailSubject: null,
  },
];

export const DEMO_LIBRARY_ANALYSES: Record<string, DemoDocumentAnalysis> = {
  [DOC1]: {
    id: DOC1,
    aiSummary: "Brief de campagne packaging pour la gamme été 2026 de Packaging Nord. Marc Girard détaille les 4 formats attendus (boîte, étui, pochette, ruban), les codes couleurs (bleu azur #0099CC, blanc cassé), et fixe une deadline de livraison au 31 mai. Budget total : 12 000 € HT.",
    aiImportanceScore: 92,
    aiPersonsMentioned: [
      { name: "Marc Girard", role: "Responsable marketing Packaging Nord" },
      { name: "Béatrice Dupont", role: "Directrice artistique Artemia" },
    ],
    aiDeadlines: [
      { text: "Maquettes validées", date: daysFromNow(8) },
      { text: "Livraison fichiers BAT", date: daysFromNow(14) },
    ],
    aiDecisions: [
      { text: "Format prioritaire : boîte cadeau 20×15×8 cm" },
      { text: "Impression offset 4 couleurs + vernis sélectif" },
    ],
    aiExtractedTaskCount: 3,
    aiAnalyzedAt: daysAgo(1),
    links: [
      { entityType: "contact", entityId: C2, label: "Marc Girard", sub: "Packaging Nord" },
      { entityType: "project", entityId: P2, label: "Campagne lancement été 2026", sub: null },
    ],
  },
  [DOC2]: {
    id: DOC2,
    aiSummary: "Contrat de prestation graphique entre Artemia Group (client) et Béatrice Dupont freelance pour la refonte du site vitrine. Durée : 3 mois. Montant : 9 500 € HT. Clause de confidentialité et propriété intellectuelle incluse. Signé des deux parties le 3 mai 2026.",
    aiImportanceScore: 87,
    aiPersonsMentioned: [
      { name: "Béatrice Dupont", role: "Prestataire" },
      { name: "Éric Morvan", role: "DG Artemia Group" },
    ],
    aiDeadlines: [
      { text: "Livraison finale du site", date: daysFromNow(18) },
      { text: "Acompte 30 % dû à la signature", date: daysAgo(10) },
    ],
    aiDecisions: [
      { text: "Propriété des sources transférée après solde complet" },
    ],
    aiExtractedTaskCount: 1,
    aiAnalyzedAt: daysAgo(3),
    links: [
      { entityType: "project", entityId: P1, label: "Refonte site web Artemia Group", sub: null },
    ],
  },
  [DOC3]: {
    id: DOC3,
    aiSummary: "Notes de réunion avec Sophie Martin (logistique fournisseurs). Points clés : délai livraison papier couché repoussé à J+12, devis emballage validé sous réserve d'un ajustement grammage. Prochaine réunion prévue dans 2 semaines.",
    aiImportanceScore: 74,
    aiPersonsMentioned: [
      { name: "Sophie Martin", role: "Responsable logistique fournisseurs" },
    ],
    aiDeadlines: [
      { text: "Ajustement devis emballage à confirmer", date: daysFromNow(4) },
    ],
    aiDecisions: [
      { text: "Papier couché 135g/m² retenu pour l'ensemble de la gamme" },
    ],
    aiExtractedTaskCount: 2,
    aiAnalyzedAt: daysAgo(2),
    links: [],
  },
};

export const DEMO_LIBRARY_TASKS: Record<string, DemoDocumentTask[]> = {
  [DOC1]: [
    { id: T8,  title: "Sélectionner les visuels principaux (banque d'images)", rawText: "Choisir 3–4 visuels en accord avec la charte azur/blanc.", status: "todo" },
    { id: T10, title: "Envoyer BAT à l'imprimeur", rawText: "BAT à envoyer à Printcom avant le 29 mai.", status: "todo" },
    { id: "de00000c-0000-4000-8000-000000000020", title: "Valider grammages avec le fournisseur papier", rawText: "Confirmer 135g/m² avec fournisseur sous 48h.", status: "todo" },
  ],
  [DOC2]: [
    { id: T4,  title: "Recette complète avant mise en ligne", rawText: "Tests fonctionnels complets avant livraison finale.", status: "todo" },
  ],
  [DOC3]: [
    { id: "de00000c-0000-4000-8000-000000000021", title: "Confirmer ajustement devis emballage", rawText: "Répondre à Sophie Martin sur le grammage retenu.", status: "todo" },
    { id: "de00000c-0000-4000-8000-000000000022", title: "Planifier réunion logistique J+14",          rawText: "Fixer date réunion de suivi dans 2 semaines.",      status: "todo" },
  ],
};
